import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import {
  BenchmarkExplainerType,
  DefaultWidgetNames,
  GraphTileType,
  NCExplainerType,
  WidgetType,
} from '@/api/rest/resources/types/widget';
import { BetaPill } from '@/components/Pill/BetaPill';
import {
  COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON,
  COMPLIANCE_LOGOS_BIODIVERSITY,
  COMPLIANCE_LOGOS_CARBON,
  COMPLIANCE_LOGOS_WATER,
} from '@/config/constants';
import { Logger } from '@/lib/logs/logger';

export const useDefaultProjectCarbonAGCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbon'),
    explainerType: NCExplainerType.historic_above_ground_carbon,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage'),
    popoverProps: {
      title: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.title'),
      body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.body'),
    },
  };

  // TODO: MVP-4772 - change with forest fact
  // this includes all the other places where the BG carbon facts are used in this file
  // TODO: MVP-4772 check if AGC models provide carbon equivalent facts
  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: IndicatorName.permanent_soil_carbon_total,
      average: IndicatorName.permanent_soil_carbon_per_ha,
    },
    benchmarkGraphStatFact: {
      total: IndicatorName.permanent_soil_carbon_total_benchmark,
      average: IndicatorName.permanent_soil_carbon_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total,
      average: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total_pct_change,
      average: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_below_ground_carbon,
  };

  return {
    name: DefaultWidgetNames.default_project_agc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      graphTileType: GraphTileType.historic_above_ground_carbon,
      footerProps: indicatorFooterProps,
    },
  };
};

export const useDefaultProjectCarbonBGCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.carbonStorageBG'),
    explainerType: NCExplainerType.historic_below_ground_carbon,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_CARBON,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    popoverProps: {
      title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
      body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    },
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: IndicatorName.permanent_soil_carbon_total,
      average: IndicatorName.permanent_soil_carbon_per_ha,
    },
    benchmarkGraphStatFact: {
      total: IndicatorName.permanent_soil_carbon_total_benchmark,
      average: IndicatorName.permanent_soil_carbon_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total,
      average: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: IndicatorName.permanent_soil_carbon_previous_1y_uplift_total_pct_change,
      average: IndicatorName.permanent_soil_carbon_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_below_ground_carbon,
    showProjectionsLink: true,
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.carbonStorageBody'),
  };

  return {
    name: DefaultWidgetNames.default_project_bgc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.historic_below_ground_carbon,
      footerProps: indicatorFooterProps,
    },
  };
};

export const useDefaultProjectWHCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacity'),
    explainerType: NCExplainerType.historic_water_soil_moisture,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_WATER,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacityActualAndHistorical'),
    popoverProps: {
      title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.title'),
      body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
    },
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: IndicatorName.water_fc_total,
      average: IndicatorName.water_fc_per_ha,
    },
    benchmarkGraphStatFact: {
      total: IndicatorName.water_fc_total_benchmark,
      average: IndicatorName.water_fc_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: IndicatorName.water_fc_previous_1y_uplift_total,
      average: IndicatorName.water_fc_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: IndicatorName.water_fc_previous_1y_uplift_total_pct_change,
      average: IndicatorName.water_fc_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_water_soil_moisture,
    showProjectionsLink: true,
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.waterHoldingBody'),
  };

  return {
    name: DefaultWidgetNames.default_project_whc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.historic_water_soil_moisture,
      footerProps: indicatorFooterProps,
    },
  };
};

export const useDefaultSocialIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
    showToggle: false,
  };

  return {
    name: 'default_template_social_indicator',
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      graphTileType: GraphTileType.social_life_expectancy,
    },
  };
};

export const useDefaultProjectPOFHIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: (
      <span className='flex flex-row items-center gap-3'>
        {t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitat')}
        <BetaPill
          tooltipBody={t('shared.ncaDetail.details.biodiversity.beta.tooltip.body')}
          tooltipTitle={t('shared.ncaDetail.details.biodiversity.beta.tooltip.title')}
        />
      </span>
    ),
    explainerType: NCExplainerType.protected_on_farm_habitat,
    showToggle: false,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_BIODIVERSITY,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitatActualAndHistorical'),
    popoverProps: {
      title: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.title'),
      body: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.body'),
    },
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.protectedOnFarmHabitatBody'),
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: IndicatorName.biodiversity_buffer_zone_pct,
    },
    benchmarkGraphStatFact: {
      total: IndicatorName.biodiversity_buffer_zone_pct_benchmark_graph,
    },
    oneYearChangeStatFact: {
      total: IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    },
    oneYearTrendStatFact: {
      total: IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_change,
      average: IndicatorName.biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.protected_on_farm_habitat,
  };

  return {
    name: DefaultWidgetNames.default_project_pofh_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.protected_on_farm_habitat,
      footerProps: indicatorFooterProps,
    },
  };
};

export const DEFAULT_INDICATOR_WIDGET_PROPS_BY_NAME = {
  [DefaultWidgetNames.default_project_agc_indicator_card]: useDefaultProjectCarbonAGCIndicator,
  [DefaultWidgetNames.default_project_bgc_indicator_card]: useDefaultProjectCarbonBGCIndicator,
  [DefaultWidgetNames.default_project_whc_indicator_card]: useDefaultProjectWHCIndicator,
  [DefaultWidgetNames.default_project_pofh_indicator_card]: useDefaultProjectPOFHIndicator,
  [DefaultWidgetNames.default_project_social]: useDefaultSocialIndicator,
};

export const DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME = {
  [DefaultWidgetNames.default_recommendation_card]: () => null,
  ...DEFAULT_INDICATOR_WIDGET_PROPS_BY_NAME,
};

export const DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME = {
  [DefaultWidgetNames.default_project_agc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_bgc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_whc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_pofh_indicator_card]: () => null,
};

export const createDefaultWidgetFromSlot = (
  name: string,
  type: WidgetType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedData?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { name: string; type: WidgetType; widget_format?: any } => {
  const generator = DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME[name as keyof typeof DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME];

  if (typeof generator === 'function') {
    try {
      const widget = generator();
      if (widget) {
        return widget;
      }
    } catch (error) {
      Logger.error(`Failed to create default widget: ${name} of type: ${type}`);
      Logger.error(error as Error);
    }
  }

  return {
    name,
    type,
    ...(relatedData ? { widget_format: relatedData } : {}),
  };
};
